import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config/index.js";
import {
    Hourglass,
    Route,
    ShieldCheck,
    ShieldHalf,
    TrendingUp,
    Wallet,
    Lock,
    Landmark,
    Coins,
    LineChart
} from "lucide-react";
import GatedLink from "../components/GatedLink.jsx";
import { useWallet } from "../context/WalletContext.jsx";

// Top-of-page health strip metrics. Per May 2026 client review, the
// metric set was rewritten from the old (Runway / Collateralisation /
// TVL / Bonds / Status) line-up to the 4 measures below that map
// directly onto the protocol's system-architecture waterfall:
//
//   1. 12 Month Yield Coverage  — can the protocol pay every promised
//      coupon for the next 12 months without new inflows?
//   2. TVG Floor Defence        — is the GIL buyback reserve fully
//      stocked to absorb a sell-off?
//   3. Total Value Locked       — capital held in Term Deposits
//      (excludes the Liquidity Pool, per client).
//   4. TVG v's Market price     — speculator-facing buy/hold signal.
//
// `tone` drives the value colour via STAT_TONE_CLASS below. Coverage /
// defence metrics use the green/amber/red thresholds the client called
// out (≥90% green, <75% amber, <50% red). Static placeholders for now;
// wire to /api/solvency/health once UI shape is signed off.
const STAT_STRIP = [
    {
        label: "12 Month Yield Coverage",
        value: "101%",
        tone: "green",
        tooltip:
            "GILDer protocol is designed to ensure that even if no new users come in, and regardless of the current token price, the yield payout is still dependable for as long as possible."
    },
    {
        label: "TVG Floor Defence",
        value: "97%",
        tone: "green",
        tooltip:
            "GILDer protocol prioritises yield obligations, then it builds a 'TVG Floor Defence' to buy back $GILD token in the event that there is a big sell off and it can buy undervalued tokens. When the protocol is at maximum health the floor defence is fully stocked."
    },
    {
        label: "Total Value Locked",
        value: "$1,000,000",
        tone: "neutral",
        tooltip:
            "This is the total value of all capital in the protocol, not including what is in the Liquidity Pool."
    },
    {
        label: "TVG v's Market price",
        value: "Buy",
        tone: "buy",
        tooltip:
            "Speculators can use this differential to decide their trading strategy on owning the token. When the Market price is above TVG they should hold. If the Marketprice ever falls below the TVG, they can buy knowing the Protocol will also be buying."
    }
];

// Value-tone palette for the stat strip. Coverage/defence metrics
// switch automatically; TVG-vs-market uses `buy` / `hold`.
const STAT_TONE_CLASS = {
    green: "text-[#7bd986]",  // ≥ 90 %  — fully covered
    amber: "text-[#FBC206]",  // < 75 %  — needs attention
    red:   "text-[#ff7676]",  // < 50 %  — solvency breach
    buy:   "text-[#7bd986]",  // TVG > Market → speculators "buy"
    hold:  "text-white",      // TVG < Market → speculators "hold"
    neutral: "text-white"
};

// Per client (May 2026 Site Review): replace the old "Mechanics" cards
// with the "Asset Architecture / The Economic Engine" block. Copy is
// verbatim from the client doc; visual treatment matches the cream +
// gold palette in the supplied screenshot (deliberately *not* the mint
// green used elsewhere on the landing, per client direction).
const ASSET_CARDS = [
    {
        title: "Supply Integrity",
        body: [
            "Protocol operates with strictly capped asset supply (1 Trillion GILDer). No venture capital allocations, founder distributions, or inflationary emission schedules.",
            "Every token in circulation is mathematically accounted for and driven by market participation."
        ],
        icon: "/images/supplyIntegrity.svg"
    },
    {
        title: "Algorithmic Market Treasury Execution",
        body: [
            "The Market Treasury functions as an automated market maker governed by the Treasury Value per GILDer (TVG). Programmatically executing market sales when trading at a premium to harvest USDC reserves, and provides deterministic buy-wall support when trading at a discount."
        ],
        icon: "/images/algorithmicTreasuryExecution.svg"
    },
    {
        title: "Closed-Loop Architecture",
        body: [
            "Asset mechanics operate entirely independent of external price oracles, third-party liquidity providers, or cross-chain bridges. Risk parameters are governed exclusively by internal smart contract logic, with USDC and the Base network as the sole foundational dependencies."
        ],
        icon: "/images/closed-LoopArchitecture.svg"
    },
    {
        title: "Deterministic Value Capture",
        body: [
            "Market volatility is systematically converted into stable, fiat-denominated reserves. This harvested liquidity is strictly routed to fund yield obligations, insulating Term Deposit clients from underlying asset's price action while capturing the economic velocity."
        ],
        icon: "/images/deterministicValueCapture.svg",
        // Source SVG has heavy interior transparent padding so the visual
        // size reads much smaller than the other three — render it well
        // above the slot bounds (overflows top/bottom by ~10px each) to
        // match the perceived weight of the other icons.
        iconClass: "h-28 w-28"
    }
];

/** Marketing landing page. Full-bleed sections matching the GILDer figma. */
export default function Home() {
    return (
        <div className="w-full">
            <Hero />
            <StatsStrip />
            <InstitutionalCapital />
            <CapitalFlow />
            <SafeReserveStructure />
            <Mechanics />
            {/* "Run the Numbers" calculator moved back here (Jun 2026) — the
                standalone Protocol Structure page didn't add enough value to
                justify a separate surface, so it was removed and the widget
                returned to the bottom of the landing. BorrowAgainst and
                FinalCTA remain defined below, preserved for future reuse. */}
            <RunTheNumbers />
        </div>
    );
}

/**
 * Hero — "USDC in. USDC out. 3 Year Term Deposit"
 *
 * Replaces the original "20-27.5% Annual Yield" hero per the May 2026
 * site review. Cream + gold palette to match the Economic Engine
 * section, three product cards (Standard / Leverage / Turbo Loop)
 * with dark-green borders and a single CTA each.
 *
 * The client's reference screenshot included a dramatic underline
 * under "3 Year Term Deposit"; per their note we deliberately omit it.
 */
const HERO_PRODUCTS = [
    {
        name: "Standard",
        yield: "20%",
        body: "80% Principal segregation. Always.",
        cta: "/deposit"
    },
    {
        name: "Leverage",
        // "11%+" — 11% is the floor (post-loan-interest net yield on a
        // fully-drawn position); compounding + partial draws push it
        // higher. Per May 2026 client review.
        yield: "11%+",
        body: "Borrow up to 75% LTV against your Safe Vault. Non-amortizing structure.",
        cta: "/loans/lending"
    },
    {
        name: "Turbo",
        // "Up to 27.5%" — the full leveraged 27.5% only lands for larger
        // deposits (~$10k+); below that the $100 min-deposit loop floor
        // truncates the leverage tail so the net APR is lower. Per client
        // (Jun 2026): never show a bare "27.5%" — always "up to"/"<".
        yield: "Up to 27.5%",
        body: "Yield Boost Structure. Pick Turbo at deposit creation for the leveraged up to 27.5% APR with 80/10/9/1 routing.",
        // §V6.2 — Turbo is selected on the Deposit page now, not a
        // standalone Turbo-Loop loan page. /loans/turbo is retired.
        cta: "/deposit"
    }
];

function Hero() {
    return (
        <section className="bg-[#f6f6ec] px-5 pt-[28px] pb-[60px] md:px-[80px] md:pt-[40px] md:pb-[65px]">
            <div className="mx-auto max-w-[1260px]">
                <h1 className="text-center font-[playfair-display] text-[34px] font-medium leading-[1.15] text-[#b98c23] sm:text-[44px] md:text-[54px]">
                    USDC in. USDC out. 3 Year Term Deposit
                </h1>
                <p className="mx-auto mt-5 max-w-[760px] text-center font-manrope text-[14px] font-medium leading-[22px] text-[#102712] sm:text-[16px]">
                    A structured private debt alternative for sophisticated capital. Engineered yield.
                </p>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-3">
                    {HERO_PRODUCTS.map((p) => (
                        <div
                            key={p.name}
                            className="flex flex-col rounded-[6px] bg-[#f6f6ec] border-2 border-[#102712] px-12 py-4 w-[90%] text-center shadow-[0_6px_18px_-6px_rgba(31,54,38,0.35)]"
                        >
                            <h3 className="font-manrope text-[24px] font-bold text-[#b08b2e]">
                                {p.name}
                            </h3>
                            <p className="mt-4 font-[montserrat] text-[22px] font-medium leading-[1.25] text-[#b08b2e] sm:text-[20px]">
                                {p.yield} Uncompounded Annual Yield
                            </p>
                            <p className="mt-4 font-manrope text-[14px] font-medium leading-[18px] text-[#000000]">
                                {p.body}
                            </p>
                            <div className="mt-auto pt-5">
                                <GatedLink
                                    to={p.cta}
                                    className="inline-flex items-center justify-center rounded-[4px] bg-[#223e2a] px-3 py-1.5 font-manrope text-[12px] font-bold text-[#d2ddd2] hover:bg-[#0E2C19] transition-colors"
                                >
                                    Structure and Details
                                </GatedLink>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/**
 * Flat full-width stats strip sitting directly under the hero. Inline
 * "Label: Value" format with gold labels, white bold values, separated
 * by thin gold dividers. A tiny gold triangle hangs off the bottom-right
 * corner — matches the client's reference screenshot.
 */
function StatsStrip() {
    return (
        <section className="bg-[#213e28] overflow-hidden">
            <div className="mx-auto flex w-full max-w-[1800px] flex-col items-stretch divide-y divide-[#9a8955]/25 px-4 sm:flex-row sm:divide-y-0 sm:divide-x sm:divide-[#9a8955]/25 md:px-8">
                {STAT_STRIP.map((stat) => (
                    <StatStripItem key={stat.label} stat={stat} />
                ))}
            </div>
        </section>
    );
}

/**
 * One cell in the top-of-page health strip. Renders the label, the
 * value (coloured by `tone` per STAT_TONE_CLASS), and a "?" badge
 * that toggles a popover with the metric explainer on click and
 * shows the same text on hover via the native `title` attribute.
 *
 * The click-popover is the primary affordance (per client review:
 * "clickable '?' with descriptions that pop up / hover") — title
 * covers desktop hover for users who don't click, and screen readers
 * pick it up via aria-label.
 */
function StatStripItem({ stat }) {
    const [open, setOpen] = useState(false);
    const valueClass = STAT_TONE_CLASS[stat.tone] || STAT_TONE_CLASS.neutral;
    return (
        <div className="relative flex flex-1 items-center justify-center gap-2 px-4 py-3 text-center font-manrope text-[11px] sm:text-[12px] md:text-[13px] whitespace-nowrap">
            <span className="font-medium text-[#c4b050]">
                {stat.label}:
            </span>
            <span className={`font-bold ${valueClass}`}>
                {stat.value}
            </span>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                onBlur={() => setOpen(false)}
                aria-label={stat.tooltip}
                aria-expanded={open}
                title={stat.tooltip}
                className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-[#c4b050]/20 text-[9px] font-bold text-[#c4b050] hover:bg-[#c4b050]/40 transition-colors"
            >
                ?
            </button>
            {open ? (
                <div
                    role="tooltip"
                    className="absolute left-1/2 top-full z-30 mt-2 w-72 -translate-x-1/2 rounded-[10px] bg-white px-3 py-2.5 text-left text-[11px] leading-snug text-[#10281b] shadow-xl ring-1 ring-[#bfb67b]"
                >
                    <div className="mb-1 font-manrope text-[11px] font-bold text-[#1d3e28]">
                        {stat.label}
                    </div>
                    <div className="text-[10.5px] text-[#10281b]/85">
                        {stat.tooltip}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/**
 * "How Capital Moves Through GILDer" — a single, deterministic flow of a
 * Standard Term Deposit, from USDC entry through to USDC yield support.
 *
 * Replaces the client's "a bit rubbish" AI-generated flow graphic. Sits
 * as a dark-green feature band *between* the cream Institutional Capital
 * block and the white Safe Reserve Structure block, so it reads as a
 * distinct module and gives the landing a rhythm of cream → green → white.
 * Green band + gold accents match the StatsStrip palette (#213e28) so it
 * stays on-brand rather than introducing a new surface colour.
 *
 * Two layouts share one `FLOW_STEPS` data source: a horizontal stepper on
 * lg+ with an animated gold rail (suggesting capital moving through the
 * route), and a vertical timeline on small screens. The animation honours
 * prefers-reduced-motion (see `.gilder-flow-line` in index.css).
 */
const FLOW_STEPS = [
    {
        Icon: Wallet,
        lead: "",
        title: "Deposit USDC",
        body: "A three-year fixed-term entry. USDC in, USDC out, with no other asset to hold."
    },
    {
        Icon: Lock,
        lead: "80%",
        title: "Segregated Reserve",
        body: "Principal is ring-fenced in a closed-loop vault and never mobilised without your instruction."
    },
    {
        Icon: Landmark,
        lead: "20%",
        title: "Active Treasury",
        body: "The operational tranche: internal market-making and the Distribution Reserve that funds yield."
    },
    {
        Icon: Coins,
        lead: "",
        title: "Programmatic Demand",
        body: "Active capital creates structural demand against a strictly capped token supply."
    },
    {
        Icon: LineChart,
        lead: "",
        title: "USDC Yield Support",
        body: "Rules-based payouts support your yield, reliably, across the full three-year term."
    }
];

function CapitalFlow() {
    return (
        <section className="bg-[#213e28] px-5 py-[60px] md:px-[80px] md:py-[80px]">
            <div className="mx-auto max-w-[1280px]">
                <div className="text-center">
                    <span className="font-manrope text-[11px] font-bold uppercase tracking-[0.28em] text-[#e7c14f] sm:text-[12px]">
                        The Standard Term Deposit
                    </span>
                    <h2 className="mt-3 font-serif text-[30px] font-medium leading-[1.15] text-[#f6f6ec] sm:text-[40px] md:text-[48px]">
                        How Capital Moves Through GILDer
                    </h2>
                    <p className="mx-auto mt-4 max-w-[620px] font-manrope text-[14px] leading-[22px] text-[#f6f6ec]/70">
                        One deposit, one deterministic path. Every dollar follows the
                        same programmatic route — from entry, through a ring-fenced
                        reserve, to reliable USDC yield.
                    </p>
                </div>

                {/* Horizontal stepper — lg and up. The animated gold rail
                    sits behind the icon badges; each badge wears a thick
                    #213e28 ring so the rail reads as discrete segments
                    flowing between the nodes. */}
                <div className="mt-16 hidden lg:block">
                    <div className="relative">
                        <div className="absolute left-[10%] right-[10%] top-8 h-[2px] -translate-y-1/2 overflow-hidden rounded-full bg-[#f6f6ec]/12">
                            <div className="gilder-flow-line h-full w-full" aria-hidden="true" />
                        </div>
                        <ol className="relative grid grid-cols-5 gap-5">
                            {FLOW_STEPS.map((s, i) => (
                                <li key={s.title} className="flex flex-col items-center text-center">
                                    <div className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-[#e7c14f] text-[#1d3e28] shadow-[0_6px_18px_-6px_rgba(0,0,0,0.5)] ring-[6px] ring-[#213e28]">
                                        <s.Icon className="h-6 w-6" strokeWidth={1.9} />
                                    </div>
                                    <div className="mt-5 font-serif text-[12px] font-bold uppercase tracking-[0.22em] text-[#e7c14f]">
                                        Step {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h3 className="mt-2 font-serif text-[19px] font-semibold leading-[1.25] text-[#f6f6ec]">
                                        {s.lead ? (
                                            <span className="text-[#e7c14f]">{s.lead} </span>
                                        ) : null}
                                        {s.title}
                                    </h3>
                                    <p className="mt-2.5 font-manrope text-[12.5px] leading-[19px] text-[#f6f6ec]/65">
                                        {s.body}
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>

                {/* Vertical timeline — below lg. A static gold-tinted rail
                    runs down the left edge; badges sit on the rail. */}
                <div className="mt-12 lg:hidden">
                    <ol className="relative ml-4 space-y-8 border-l-2 border-[#f6f6ec]/15">
                        {FLOW_STEPS.map((s, i) => (
                            <li key={s.title} className="relative pl-8">
                                <span className="absolute -left-[1.375rem] top-0 flex h-10 w-10 items-center justify-center rounded-full bg-[#e7c14f] text-[#1d3e28] ring-4 ring-[#213e28]">
                                    <s.Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
                                </span>
                                <div className="font-serif text-[11px] font-bold uppercase tracking-[0.22em] text-[#e7c14f]">
                                    Step {String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="mt-1 font-serif text-[18px] font-semibold leading-[1.25] text-[#f6f6ec]">
                                    {s.lead ? (
                                        <span className="text-[#e7c14f]">{s.lead} </span>
                                    ) : null}
                                    {s.title}
                                </h3>
                                <p className="mt-2 font-manrope text-[13px] leading-[20px] text-[#f6f6ec]/65">
                                    {s.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Stress-case footer — carried over from the client's
                    original graphic, restyled as a bordered note. */}
                <div className="mx-auto mt-14 max-w-[780px] rounded-xl border border-[#e7c14f]/25 bg-[#f6f6ec]/[0.03] px-5 py-4 text-center">
                    <p className="font-manrope text-[12.5px] leading-[20px] text-[#f6f6ec]/75">
                        <span className="font-bold uppercase tracking-wider text-[#e7c14f]">
                            Stress case —{" "}
                        </span>
                        if growth slows or stops, the protocol prioritises reserve
                        integrity and the orderly management of existing obligations.
                    </p>
                </div>
            </div>
        </section>
    );
}

/**
 * "Engineered for Institutional Capital" — replaces the old mint-green
 * "Three numbers that matter" block per the May 2026 site review. Cream
 * + gold palette matches the Hero and Economic Engine sections so the
 * landing reads as a single coherent system instead of jumping between
 * mint and cream surfaces.
 *
 * Three sub-sections, each with a serif sub-heading, a short gold lead
 * sentence, and a body paragraph. Copy is verbatim from the client doc.
 */
const INSTITUTIONAL_SECTIONS = [
    {
        title: "Yield Prioritisation over Asset Inflation",
        body: "Prioritising sustainable yield generation over speculative asset appreciation. Digital asset models often rely on continuous, unsustainable asset inflation. GILDer’s automated market maker systematically harvests premium volatility, converting market demand directly into USDC reserves. Speculative volatility is deliberately suppressed to secure the yield runway for long-term clients.",
        icon: "trending"
    },
    {
        title: "Duration Alignment and Structural Moats",
        body: "Strategic friction protects committed capital. The three-year term and early redemption penalties are engineered to prevent sudden liquidity shocks and ensure short-term market movements do not dilute the returns of long-term participants. Early exit penalties flow directly into the protocol reserves, structurally rewarding commitment.",
        icon: "hourglass"
    },
    {
        title: "Absolute Segregation and Principal Defence",
        body: "Principal reserves are ring-fenced and never exposed to external credit or third-party market risk. GILDer strictly prohibits the commingling and rehypothecation of client principal to external entities. The 80 percent Safe Vault functions exclusively as a closed-loop reserve. When clients engage the Leverage or Turbo structures, they are collateralising their own segregated capital against the protocol's internal liquidity. The protocol maintains only two external dependencies: USDC and the Base network.",
        icon: "shield"
    }
];

const ROMAN = ["I", "II", "III", "IV", "V"];

function InstitutionalCapital() {
    return (
        <section className="bg-[#f6f6ec] px-5 py-[60px] md:px-[80px] md:py-[80px]">
            <div className="mx-auto max-w-[1280px]">
                <h2 className="text-center font-serif text-[32px] font-medium leading-[1.15] text-[#b98c23] sm:text-[42px] md:text-[52px]">
                    Engineered for Institutional Capital.
                </h2>

                {/* Magazine-style three-column block: top + bottom hairlines
                    span the full width, columns are separated by thin
                    vertical dividers. Each column has a roman numeral on
                    the left and an icon on the right, then a serif title,
                    a short gold accent, and the body paragraph. */}
                <div className="mt-10 border-t border-[#213e28]/15">
                    <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x md:divide-[#213e28]/15">
                        {INSTITUTIONAL_SECTIONS.map((s, idx) => (
                            <div key={s.title} className="flex flex-col p-6 md:p-8">
                                <div className="flex items-start justify-between">
                                    <span className="font-serif text-[18px] font-medium text-[#b08b2e]">
                                        {ROMAN[idx]}.
                                    </span>
                                    <span className="text-[#b08b2e]">
                                        <SectionIcon kind={s.icon} />
                                    </span>
                                </div>

                                <h3 className="mt-4 font-serif text-[20px] font-semibold leading-[1.25] text-[#b08b2e] sm:text-[22px]">
                                    {s.title}
                                </h3>

                                <span
                                    className="mt-3 inline-block h-px w-10 bg-[#9a8955]"
                                    aria-hidden="true"
                                />

                                <ReadMoreText
                                    text={s.body}
                                    lines={3}
                                    className="mt-4 font-manrope text-[13px] font-medium leading-[20px] text-[#000500]"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-[#213e28]/15" />
                </div>
            </div>
        </section>
    );
}

/**
 * Collapsible paragraph used inside the Institutional Capital columns.
 * Clamps to `lines` (default 3) of visible text and reveals the rest
 * behind a small gold "Read More" / "Read Less" toggle. Uses the
 * `-webkit-line-clamp` CSS so the clamp count is exact, regardless of
 * the body font's line-height.
 */
function ReadMoreText({ text, lines = 3, className = "" }) {
    const [expanded, setExpanded] = useState(false);
    const clampStyle = expanded
        ? undefined
        : {
              display: "-webkit-box",
              WebkitLineClamp: lines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
          };
    return (
        <div className="flex flex-1 flex-col">
            <p className={className} style={clampStyle}>
                {text}
            </p>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-2 inline-flex w-fit items-center gap-1 font-manrope text-[11px] font-semibold uppercase tracking-widest text-[#9a8955] hover:underline"
            >
                {expanded ? "Read Less" : "Read More"}
                <svg
                    className={`h-2.5 w-2.5 transition-transform ${
                        expanded ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="4 7 10 13 16 7" />
                </svg>
            </button>
        </div>
    );
}

/**
 * Three icons used by the Institutional Capital columns. Drawn in
 * `currentColor` so the parent span controls the gold tint.
 */
function SectionIcon({ kind }) {
    if (kind === "trending") {
        return (
                <TrendingUp className="h-5 w-5" />

        );
    }
    if (kind === "hourglass") {
        return (
                <Hourglass className="h-5 w-5"/>
        );
    }
    if (kind === "shield") {
        return (
                <ShieldHalf className="h-5 w-5" />
        );
    }
    return null;
}

/**
 * Two-card explainer pair sitting beside the Allocation Strategy panel,
 * per the May 2026 client review. Replaces the old "Your deposit, split
 * by design." copy block with the screenshot-matched layout: tinted
 * cream card, thick coloured left rail (dark green / gold), small inline
 * icon next to an uppercase eyebrow, bold serif sub-heading, body copy.
 */
/**
 * Allocation Strategy panel rows. Per May 2026 client review, the
 * home-page surface deliberately collapses the full 80 / 10 / 9 / 1
 * routing waterfall into a simpler 80 / 20 view — the home page is
 * marketing-facing, and the 4-slice breakdown is more detail than
 * is helpful for a first-time visitor. The granular split is still
 * visible inside the app (Deposit page + TdDetail "Capital Routing"
 * card) for users who want it.
 */
const ALLOCATIONS = [
    {
        label: "Safe Reserve",
        sub: "Ring-fenced principal",
        value: "80%",
        dot: "bg-vb-dark",
        sliceColor: "#1d3e28"
    },
    {
        label: "Actively Earning Your Yield",
        sub: "Token buy + Distribution Reserve + Liquidity",
        value: "20%",
        dot: "bg-vb-accent ring-1 ring-vb-dark",
        sliceColor: "#FBC206"
    }
];

const RESERVE_CARDS = [
    {
        eyebrow: "Deterministic Capital Routing",
        heading: "Immutable allocation by design.",
        body:
            "All Term Deposits follow strict, programmatic routing sequence. 80 percent of principal is deterministically ring-fenced and cannot be mobilized without explicit client instruction. 20 percent constitutes the active operational tranche. This capital acts as the protocol's engine, systematically acquiring protocol tokens and capitalising the Distribution Reserve to fund the yield obligations.",
        icon: "routing",
        railClass: "border-l-[6px] border-[#1d3e28]"
    },
    {
        eyebrow: "Zero-Growth Resiliency",
        heading: "Structural predictability in adverse conditions.",
        body:
            "Should organic growth cease entirely, the protocol is engineered to initiate a structured wind-down sequence. This sequence systematically halts operational expenditure and prioritises the orderly redemption of ongoing obligations until the protocol is fully unwound.",
        bodyBold:
            "The segregated principal within the Safe Vault remains strictly insulated throughout.",
        icon: "shield",
        railClass: "border-l-[6px] border-[#e7c14f]"
    }
];

function SafeReserveStructure() {
    return (
        <section className="w-full border-y-2 border-[#213e28] bg-[#FFFFFF] px-5 py-[48px] sm:py-[60px] md:px-[120px] md:py-[80px]">
            <div className="mx-auto grid max-w-[1680px] grid-cols-1 items-stretch gap-8 md:grid-cols-3 md:gap-[32px]">
                {RESERVE_CARDS.map((card) => (
                    <article
                        key={card.eyebrow}
                        className={`flex h-full flex-col rounded-[8px] bg-[#f4f4ec] px-6 py-6 sm:px-7 sm:py-7 ${card.railClass}`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="text-[#1d3e28]">
                                <ReserveIcon kind={card.icon} />
                            </span>
                            <span className="font-manrope text-[12px] font-bold uppercase tracking-[0.2em] text-[#1d3e28]">
                                {card.eyebrow}
                            </span>
                        </div>
                        <h3 className="mt-4 font-serif text-[22px] font-bold leading-[1.2] text-[#1d3e28] sm:text-[24px]">
                            {card.heading}
                        </h3>
                        <p className="mt-4 font-manrope text-[14px] leading-[22px] text-[#1d3e28]">
                            {card.body}
                        </p>
                        {card.bodyBold ? (
                            <p className="mt-3 font-manrope text-[14px] leading-[22px] text-[#1d3e28]">
                                {card.bodyBold}
                            </p>
                        ) : null}
                    </article>
                ))}

                {/* `self-start` so the Allocation card sizes to its
                    own content instead of stretching to match the two
                    text-heavy sibling cards. Without it the panel
                    rendered ~30 % taller than its content needed,
                    leaving a visible empty band below the labels. */}
                <div className="flex flex-col rounded-2xl bg-vb-accent p-5 sm:p-7 self-start">
                    <div className="flex items-center gap-2">
                        <span className="text-[#1d3e28]">
                            <img
                                src="/images/holdingsAndPayable.png"
                                alt=""
                                aria-hidden="true"
                                className="h-4 w-4 "
                            />
                        </span>
                        <span className="font-manrope text-[12px] font-bold uppercase tracking-[0.2em] text-[#1d3e28]">
                            100% Fully Distributed
                        </span>
                    </div>
                    <h3 className="mt-4 font-serif text-[22px] font-bold leading-[1.2] text-[#1d3e28] sm:text-[24px]">
                        Allocation Strategy
                    </h3>

                    {/* Donut chart — 80 / 20 split. The dark slice is
                        the ring-fenced Safe Reserve; the gold slice is
                        the active capital that funds yield. */}
                    <div className="mt-6 flex justify-center">
                        <AllocationDonut allocations={ALLOCATIONS} />
                    </div>

                    <div className="mt-6 flex flex-col gap-3">
                        {ALLOCATIONS.map((item) => (
                            <AllocationBox key={item.label} {...item} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * Small inline glyphs for the Reserve Structure card eyebrows. Drawn in
 * `currentColor` so the parent <span> controls the tint.
 */
function ReserveIcon({ kind }) {
    if (kind === "routing") {
        return (<Route />);
    }
    if (kind === "shield") {
        return (<ShieldCheck />);
    }
    return null;
}

/**
 * Inner allocation row inside the Allocation Strategy panel — boxed
 * treatment so each item reads as its own card. Optional `sub` is a
 * one-line caption that surfaces what the bucket actually contains
 * (e.g. for the 20% "Actively Earning" slice it lists Token buy +
 * Distribution Reserve + Liquidity so the user can still see the
 * underlying composition without the full 4-slice chart).
 */
function AllocationBox({ label, value, dot, sub }) {
    return (
        <div className="flex items-center justify-between rounded-[10px] bg-[#f4f4ec] px-3 py-2 ring-1 ring-vb-text/10">
            <div className="flex items-center gap-2">
                <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden="true" />
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-vb-text">
                        {label}
                    </div>
                    {sub ? (
                        <div className="text-[9px] font-medium text-vb-text/65">
                            {sub}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="text-[14px] font-bold text-vb-text">{value}</div>
        </div>
    );
}

/**
 * 80/20 donut chart for the Allocation Strategy panel. Pure SVG so
 * there's no chart-lib dependency and the slice colours / center
 * label can be tuned inline. Each `allocations` entry supplies a
 * `value` percentage (string, e.g. "80%") and a `sliceColor` hex.
 *
 * Implementation: a single full circle stroke per slice, with
 * `strokeDasharray` cut to the slice length and `strokeDashoffset`
 * rotated to start where the previous slice ended.
 */
function AllocationDonut({ allocations }) {
    const size = 180;
    const stroke = 28;
    const radius = (size - stroke) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;

    let offset = 0;
    return (
        <svg
            viewBox={`0 0 ${size} ${size}`}
            className="h-[184px] w-[184px]"
            role="img"
            aria-label="Allocation strategy: 80% Safe Reserve, 20% Actively Earning"
        >
            {/* Background ring — visible underneath the slices so any
                rounding gap renders as a thin neutral gap rather than
                a stark white sliver. */}
            <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke="#f4f4ec"
                strokeWidth={stroke}
            />
            {allocations.map((slice) => {
                const pct = Number(String(slice.value).replace("%", "")) || 0;
                const length = (pct / 100) * circumference;
                const seg = (
                    <circle
                        key={slice.label}
                        cx={cx}
                        cy={cy}
                        r={radius}
                        fill="none"
                        stroke={slice.sliceColor}
                        strokeWidth={stroke}
                        strokeDasharray={`${length} ${circumference - length}`}
                        strokeDashoffset={-offset}
                        // Start at 12 o'clock instead of the default 3.
                        transform={`rotate(-90 ${cx} ${cy})`}
                    />
                );
                offset += length;
                return seg;
            })}
            {/* Center label — "80% / 20%" stacked. */}
            <text
                x={cx}
                y={cy - 4}
                textAnchor="middle"
                className="font-serif"
                fontSize="22"
                fontWeight="700"
                fill="#1d3e28"
            >
                80 / 20
            </text>
            <text
                x={cx}
                y={cy + 14}
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                letterSpacing="2.5"
                fill="#1d3e28"
                opacity="0.7"
            >
                SAFE / ACTIVE
            </text>
        </svg>
    );
}

/**
 * Asset Architecture — The Economic Engine.
 *
 * Replaces the old "Mechanics" block. Cream + gold treatment per the
 * client's May 2026 site review screenshot: small "ASSET ARCHITECTURE"
 * eyebrow, big serif title in gold, hairline divider, then four
 * cards laid out 1 / 2 / 4 across the breakpoints with centered icon,
 * gold serif title and dark body copy.
 */
function Mechanics() {
    // Palette per client spec (May 2026 review):
    //   bg          #f6f6ec   warm cream
    //   heading     #9a8955   muted gold
    //   body text   #000500   near-black
    //   icon        #8c8a75   muted khaki
    //   border      #000400   near-black hairline
    //   drop shadow #1f3626   dark-green tint
    return (
        <section className="bg-[#f6f6ec] px-5 py-[28px] md:px-[80px] md:py-[40px]">
            <div className="mx-auto max-w-[1280px] text-center">
                <p className="font-manrope text-[12px] font-bold uppercase tracking-[0.3em] text-[#9a8955]">
                    Asset Architecture
                </p>
                <h2 className="mt-4 font-serif text-[40px] font-medium leading-[1.1] text-[#9a8955] sm:text-[52px] md:text-[60px]">
                    The Economic Engine.
                </h2>
                <p className="mx-auto mt-5 max-w-[720px] font-manrope text-[14px] font-medium leading-[22px] text-[#000500] sm:text-[15px]">
                    An algorithmically governed asset designed exclusively to convert
                    market volatility into stable protocol reserves.
                </p>

                <div className="mx-auto mt-10 h-px max-w-[1100px] bg-[#9a8955]/30" />

                <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                    {ASSET_CARDS.map((card) => (
                        <div
                            key={card.title}
                            className="flex flex-col rounded-[6px] bg-[#f6f6ec] border-2 border-[#000400] px-5 pt-3 pb-8 text-center shadow-[0_6px_18px_-6px_rgba(31,54,38,0.35)]"
                        >
                            {/* Fixed-height slot keeps the heading baseline
                                aligned across all four cards even when one
                                icon is rendered larger than the rest. */}
                            <div className="mx-auto flex h-20 w-20 items-center justify-center">
                                <img
                                    src={card.icon}
                                    alt=""
                                    aria-hidden="true"
                                    className={`object-contain ${card.iconClass ?? "h-14 w-14"}`}
                                />
                            </div>

                            <h3 className="mt-1 font-serif text-[20px] font-semibold leading-[1.15] text-[#9a8955] sm:text-[22px]">
                                {card.title}
                            </h3>
                            {card.body.map((paragraph, i) => (
                                <p
                                    key={i}
                                    className={`font-manrope text-[12.5px] font-medium leading-[19px] text-[#000500] ${
                                        i === 0 ? "mt-4" : "mt-3"
                                    }`}
                                >
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BorrowAgainst() {
    const navigate = useNavigateGated();
    return (
        <section className="bg-vb-bg py-12 sm:py-20">
            <div className="mx-auto grid max-w-7xl gap-8 px-5 sm:px-8 md:grid-cols-2 md:items-center md:gap-10">
                <div className="rounded-2xl bg-[#CAF4D9] ring-2 ring-[#ABD8BB] p-7">
                    <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-vb-text">Borrowing Calculation</h3>
                        <span className="rounded-full bg-vb-accent/60 px-3 py-1 text-[11px] font-semibold text-vb-text">
                            Active Loan
                        </span>
                    </div>
                    <dl className="mt-6 space-y-3 text-sm">
                        <div className="flex justify-between border-b border-vb-text/20 pb-3">
                            <dt className="text-vb-text-soft">Annual Yield (Earned)</dt>
                            <dd className="font-semibold text-vb-success">+20%</dd>
                        </div>
                        <div className="flex justify-between border-b border-vb-text/20 pb-3">
                            <dt className="text-vb-text-soft">Loan Interest (Paid)</dt>
                            <dd className="font-semibold text-vb-danger">-15%</dd>
                        </div>
                        <div className="flex justify-between pt-1">
                            <dt className="font-semibold text-vb-text">Net Annual Income</dt>
                            <dd className="text-2xl font-bold text-vb-text">5.0%</dd>
                        </div>
                    </dl>
                    <button
                        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-none bg-[#FBC206] px-6 py-3 font-manrope text-[15px] font-extrabold text-[#1D3E29] transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        onClick={() => navigate("/loans/lending")}
                    >
                        Apply for Credit Line
                    </button>
                </div>

                <div>
                    <h2 className="text-3xl font-bold text-vb-text sm:text-4xl md:text-5xl">
                        Borrow Against Your
                        <br />
                        Safe Reserve.
                    </h2>
                    <p className="mt-5 text-sm leading-relaxed text-vb-text-soft">
                        Need cash without closing your position? Borrow up to 75% of your Safe Vault balance at
                        15% APR. Your bond continues earning 20%. The net result: you receive liquidity and
                        still pocket a positive spread.
                    </p>
                    <ul className="mt-5 space-y-3 text-sm text-vb-text-soft">
                        <li className="flex items-center gap-3">
                            <CheckCircle />
                            Zero liquidation risk on principal (80% reserve)
                        </li>
                        <li className="flex items-center gap-3">
                            <CheckCircle />
                            Instant approval via smart contract
                        </li>
                        <li className="flex items-center gap-3">
                            <CheckCircle />
                            Flexible repayment schedules
                        </li>
                    </ul>
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="bg-[#CAF4D9] py-12 sm:py-20">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 text-center">
                <h2 className="text-3xl font-bold text-vb-text sm:text-4xl md:text-5xl">Your wealth deserves a</h2>
                <h2 className="mt-2 text-3xl font-bold text-[#FBC206] sm:text-4xl md:text-5xl">Investment Security.</h2>
                <p className="mx-auto mt-6 max-w-2xl text-sm text-vb-text-soft">
                    Join sophisticated investors building structured yield on structurally quarantined capital with GILDer.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-[20px]">
                    <GatedLink
                        to="/deposit"
                        className="inline-flex items-center justify-center gap-2 rounded-[8px] bg-[#FBC206] px-6 py-3 font-manrope text-[15px] font-extrabold text-[#1D3E29] transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    >
                        Connect Wallet
                        <ArrowIcon />
                    </GatedLink>
                    <GatedLink
                        to="/support"
                        className="rounded-[8px] border-2 border-[#1D3E29] px-6 py-3 font-manrope text-[15px] font-extrabold text-[#1D3E29] hover:bg-[#1D3E29] hover:text-white transition-colors inline-flex items-center gap-2"
                    >
                        Talk to support
                        <ArrowIcon />
                    </GatedLink>
                </div>
            </div>
        </section>
    );
}

/**
 * Hook variant of GatedLink for buttons that don't render an anchor.
 * Returns a function: if a wallet is connected the destination is opened
 * via react-router; otherwise the connect modal opens with the destination
 * remembered for after a successful connect.
 */
function useNavigateGated() {
    const { address, requestConnect } = useWallet();
    const navigate = useNavigate();
    return (destination) => {
        if (address) {
            navigate(destination);
            return;
        }
        requestConnect(destination);
    };
}

function ArrowIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

function TrendingIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 17 9 11 13 15 21 7" />
            <polyline points="14 7 21 7 21 14" />
        </svg>
    );
}

function CheckCircle() {
    return (
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-1 ring-vb-text-soft text-vb-text-soft">
            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="4 11 8 15 16 6" />
            </svg>
        </span>
    );
}

/* -------------------------------------------------------------------------- */
/*  Run the numbers — interactive yield calculator                            */
/*                                                                            */
/*  Lived on the standalone Protocol Structure page; moved back to the        */
/*  bottom of the landing (Jun 2026) when that page was retired.              */
/* -------------------------------------------------------------------------- */

const SLIDER_MAX = 500000;

// Per-tab projection config (client, Jun 2026):
//   - Each structure projects at its OWN headline rate now (Standard 20%,
//     Turbo up-to-27.5%, Leverage 11% net) rather than a single 20% for all.
//   - Turbo's slider STARTS at $10k: below that the $100 min-deposit loop
//     floor truncates the leverage tail so the full 27.5% doesn't apply, and
//     quoting 27.5% on a sub-$10k figure would overstate it.
const TAB_CONFIG = {
    standard: { rate: 0.2, min: 1000 },
    turbo: { rate: 0.275, min: 10000 },
    leverage: { rate: 0.11, min: 1000 }
};

function RunTheNumbers() {
    const [tab, setTab] = useState("turbo");
    const [amount, setAmount] = useState(TAB_CONFIG.turbo.min);
    const { rate, min: sliderMin } = TAB_CONFIG[tab];

    // Switching structures clamps the deposit up to the new tab's minimum
    // (Turbo's $10k floor) so the projection never shows a below-minimum
    // amount for the selected product.
    function selectTab(next) {
        setTab(next);
        const nextMin = TAB_CONFIG[next].min;
        setAmount((prev) => (prev < nextMin ? nextMin : prev));
    }

    // Scale labels: first tick tracks the active tab's minimum so the left
    // edge always matches the slider's start ($1k, or $10k for Turbo).
    const sliderTicks = [sliderMin, 100000, 250000, 500000];

    const numbers = useMemo(() => {
        const annual = amount * rate;
        // Available to withdraw on a Leverage position: borrow up to the max
        // LTV (75%) against the Safe Vault (80% of principal) → 60% of deposit.
        const available =
            amount *
            (config.protocol.safeVaultBps / 10000) *
            (config.protocol.maxLtvBps / 10000);
        return {
            daily: annual / 365,
            monthly: annual / 12,
            annual,
            threeYear: annual * 3,
            // "Total After Year 3" = principal + 3 years of return.
            total: amount * (1 + rate * 3),
            available
        };
    }, [amount, rate]);

    return (
        <section className="border-t-2 border-[#213e28] bg-[#f6f6ec] px-5 py-[48px] md:px-[80px] md:py-[64px]">
            <div className="mx-auto max-w-[1280px]">
                <p className="text-center font-manrope text-[12px] font-bold uppercase tracking-[0.3em] text-[#b98c23]">
                    Run the Numbers
                </p>
                <h2 className="mt-3 text-center font-serif text-[28px] font-medium leading-[1.15] text-[#b98c23] sm:text-[34px] md:text-[40px]">
                    Pick a structure. See the math.
                </h2>
                <p className="mx-auto mt-3 max-w-[640px] text-center font-manrope text-[14px] font-medium text-[#102712]">
                    Transparency is our primary asset. Explore your projected outcomes.
                </p>

                <div className="mx-auto mt-8 max-w-5xl">
                    <div className="grid grid-cols-1 gap-1 rounded-[10px] border-2 border-[#213e28] bg-[#f6f6ec] p-1 sm:grid-cols-3">
                        <TabButton active={tab === "standard"} onClick={() => selectTab("standard")}>
                            Standard Bond (20% APR)
                        </TabButton>
                        <TabButton active={tab === "turbo"} onClick={() => selectTab("turbo")}>
                            Turbo Bond (up to 27.5% APR)
                        </TabButton>
                        <TabButton active={tab === "leverage"} onClick={() => selectTab("leverage")}>
                            Leverage (11%+)
                        </TabButton>
                    </div>

                    <div className="mt-5 rounded-[16px] border-2 border-[#102712] bg-[#f6f6ec] p-5 shadow-[0_6px_18px_-10px_rgba(31,54,38,0.35)]">
                        <div className="rounded-[14px] bg-[#213e28] px-5 py-3 text-white">
                            <div className="flex items-center justify-between">
                                <span className="font-manrope text-[11px] font-semibold uppercase tracking-widest text-[#c4b050]">
                                    Deposit Amount (USDC)
                                </span>
                                <span className="font-manrope text-[20px] font-bold text-white">
                                    ${amount.toLocaleString()}
                                </span>
                            </div>
                            <input
                                type="range"
                                min={sliderMin}
                                max={SLIDER_MAX}
                                step={1000}
                                value={amount}
                                onChange={(event) => setAmount(Number(event.target.value))}
                                className="mt-2 w-full accent-[#FFC400]"
                                aria-label="Deposit amount"
                            />
                            {/* Tick labels positioned at their true linear
                                offset along the track so each sits under the
                                thumb at its own value. First/last labels align
                                to the track edges; interior labels centre on
                                their tick. */}
                            <div className="relative mt-1 h-4 font-manrope text-[11px] text-[#FFC400]">
                                {sliderTicks.map((tick, i) => {
                                    const pct =
                                        ((tick - sliderMin) /
                                            (SLIDER_MAX - sliderMin)) *
                                        100;
                                    const transform =
                                        i === 0
                                            ? "translateX(0)"
                                            : i === sliderTicks.length - 1
                                            ? "translateX(-100%)"
                                            : "translateX(-50%)";
                                    return (
                                        <span
                                            key={tick}
                                            className="absolute top-0 whitespace-nowrap"
                                            style={{ left: `${pct}%`, transform }}
                                        >
                                            ${tick.toLocaleString()}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <ResultCell label="Daily Payout" value={`$${numbers.daily.toFixed(2)}`} />
                            <ResultCell
                                label="Monthly Interest"
                                value={`$${Math.round(numbers.monthly).toLocaleString()}`}
                            />
                            <ResultCell
                                label="3-Year Return"
                                value={`$${Math.round(numbers.threeYear).toLocaleString()}`}
                            />
                            <ResultCell
                                label="Total After Year 3"
                                value={`$${Math.round(numbers.total).toLocaleString()}`}
                            />
                            {/* Leverage surfaces one extra figure: how much USDC
                                you can draw against the position (60% of deposit). */}
                            {tab === "leverage" ? (
                                <div className="rounded-[12px] border-2 border-[#b98c23] bg-[#fbf3d9] px-5 py-3 shadow-[0_4px_12px_-8px_rgba(31,54,38,0.35)] md:col-span-2">
                                    <div className="font-manrope text-[11px] font-semibold uppercase tracking-widest text-[#9a7d1f]">
                                        Maximum Withdrawal
                                    </div>
                                    <div className="mt-1 font-manrope text-[18px] font-bold text-[#102712]">
                                        ${Math.round(numbers.available).toLocaleString()}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <p className="mt-3 text-center font-manrope text-[11px] font-medium leading-[1.5] text-[#9a8955]">
                            The contract calculates a target return annually. Payments are made
                            in USDC subject to available protocol reserves. No returns are
                            guaranteed.
                            {tab === "leverage" ? (
                                <>
                                    {" "}
                                    Leverage mirrors the Standard structure and lets you draw
                                    up to 75% LTV against your 80% Safe Vault.
                                </>
                            ) : null}
                        </p>

                        <div className="mt-5 flex justify-center">
                            <GatedLink
                                to="/deposit"
                                className="inline-flex min-w-[220px] items-center justify-center gap-2 rounded-[9px] bg-[#213e28] px-12 py-2 font-manrope text-[14px] font-black text-white transition-all hover:-translate-y-0.5 hover:bg-[#0e2c19] hover:shadow-lg"
                            >
                                Invest Now
                                <ArrowIcon />
                            </GatedLink>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function TabButton({ active, onClick, children }) {
    return (
        <button
            className={`rounded-[7px] px-4 py-2 font-manrope text-[13px] font-bold leading-[16px] transition-colors ${
                active
                    ? "bg-[#213e28] text-white"
                    : "bg-transparent text-[#102712] hover:bg-[#213e28]/10"
            }`}
            onClick={onClick}
        >
            {children}
        </button>
    );
}

function ResultCell({ label, value }) {
    return (
        <div className="rounded-[12px] border-2 border-[#102712] bg-[#f6f6ec] px-5 py-3 shadow-[0_4px_12px_-8px_rgba(31,54,38,0.35)]">
            <div className="font-manrope text-[11px] font-semibold uppercase tracking-widest text-[#9a8955]">
                {label}
            </div>
            <div className="mt-1 font-manrope text-[18px] font-bold text-[#102712]">{value}</div>
        </div>
    );
}
